import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import CredibilityStrip from "@/components/CredibilityStrip";
import CTASection from "@/components/CTASection";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import Testimonials from "@/components/Testimonials";
import { CATEGORIES } from "@/lib/siteData";

const HomePage = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[88vh]">
          <div className="lg:col-span-6 flex flex-col justify-center px-6 md:px-10 lg:px-16 py-16 lg:py-24 bg-ivory">
            <div className="max-w-xl reveal">
              <div className="overline mb-6">Modular Furniture · Made in India</div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-black font-light">
                Crafting spaces
                <br />
                <span className="italic text-navy">that inspire.</span>
              </h1>
              <p className="mt-7 text-base md:text-lg text-black/70 leading-relaxed max-w-md">
                Modular kitchens, wardrobes, and office storage — precision-manufactured, delivered in 30 days or less.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/kitchen"
                  data-testid="hero-cta-explore"
                  className="inline-flex items-center gap-3 bg-coral hover:bg-[#d66a54] text-white px-7 py-3.5 tracking-wide transition-colors group"
                >
                  <span>Explore Collections</span>
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-cta-factory"
                  className="inline-flex items-center gap-2 text-navy border-b border-navy/40 hover:border-navy px-1 py-1 tracking-wide"
                >
                  <span>See the production floor</span>
                  <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative min-h-[380px]">
            <img
              src="/hero-kitchen.webp"
              alt="Warm minimalist modular kitchen — signature collection"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-navy/30" />
            <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 bg-white/95 backdrop-blur-sm px-5 py-4 max-w-xs border-l-2 border-coral">
              <div className="overline text-teal">Signature Kitchen</div>
              <div className="font-display text-lg md:text-xl mt-1 text-black leading-snug">White laminate, warm oak accents</div>
            </div>
          </div>
        </div>
      </section>

      <CredibilityStrip />

      {/* CATEGORIES */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
            <div className="max-w-2xl reveal">
              <div className="overline mb-4">What we manufacture</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light">
                Four categories.<br />
                <span className="italic text-teal">One production floor.</span>
              </h2>
            </div>
            <p className="text-black/60 md:text-right md:max-w-sm reveal">
              Every panel begins on the same panel line — from a compact kitchen to a boardroom credenza. That's how we hold quality steady.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {CATEGORIES.map((c, i) => (
              <Link
                key={c.slug}
                to={`/${c.slug}`}
                data-testid={`home-category-${c.slug}`}
                className="group relative overflow-hidden reveal"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="relative h-72 md:h-[420px] overflow-hidden">
                  <img
                    src={c.hero}
                    alt={c.name}
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white flex justify-between items-end">
                    <div>
                      <div className="overline text-gold">{c.tagline}</div>
                      <div className="font-display text-2xl md:text-3xl mt-2 leading-tight">{c.name}</div>
                    </div>
                    <div className="w-11 h-11 flex items-center justify-center border border-white/50 group-hover:bg-coral group-hover:border-coral transition-colors">
                      <ArrowUpRight size={18} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="bg-ivory py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
            <div className="max-w-2xl reveal">
              <div className="overline mb-4">Before &amp; After</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light">
                One room.<br />
                <span className="italic text-navy">Two lives.</span>
              </h2>
            </div>
            <p className="text-black/60 md:text-right md:max-w-sm reveal">
              Drag the handle to see the transformation — from the existing kitchen to a completely rebuilt Tarush space.
            </p>
          </div>

          <div className="reveal">
            <BeforeAfterSlider
              beforeSrc="/naresh-before.jpeg"
              afterSrc="/naresh-after.jpeg"
              beforeLabel="Before"
              afterLabel="After"
            />
          </div>

          <div className="mt-6 md:mt-8 flex items-center gap-3 text-sm text-black/60 reveal">
            <span className="inline-block w-8 h-px bg-teal" />
            <span>A recent kitchen fit-out — measured, milled, installed in 30 days.</span>
          </div>
        </div>
      </section>

      {/* FACTORY TEASER */}
      <section className="bg-offwhite py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5 reveal">
            <div className="overline mb-4">Built to scale</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] font-light text-black">
              <span className="italic text-navy">Real accountability.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/70 leading-relaxed">
              Every polished catalog photo is backed by real infrastructure — 7,000 sq. ft. of manufacturing floor, 15+ craftsmen, and precision machinery driving every panel we produce. When you commit to us — as an architect, a designer, or a dealer — you're committing to a real supply chain, not a middleman.
            </p>
            <Link
              to="/about"
              data-testid="home-factory-link"
              className="mt-8 inline-flex items-center gap-2 text-navy border-b border-navy/40 hover:border-navy py-1 tracking-wide"
            >
              <span>Take the factory tour</span>
              <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7 reveal" style={{ transitionDelay: "120ms" }}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              {[
                { number: "7,000", suffix: "sq. ft.", label: "Manufacturing floor" },
                { number: "15+", suffix: "", label: "Craftsmen on the floor" },
                { number: "European", suffix: "", label: "Precision machinery" },
              ].map((s, i) => (
                <div
                  key={s.label}
                  className="bg-white border-t-4 border-teal p-6 md:p-7 aspect-[4/5] flex flex-col justify-between"
                  data-testid={`home-factory-stat-${i}`}
                >
                  <div className="overline">0{i + 1}</div>
                  <div>
                    <div className="font-display text-3xl md:text-4xl leading-[1.05] tracking-tight text-navy">
                      {s.number}
                      {s.suffix && (
                        <span className="text-lg md:text-xl text-teal ml-1 tracking-wide">
                          {s.suffix}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 text-sm md:text-base text-black/70 leading-snug">
                      {s.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Testimonials />

      <CTASection
        eyebrow="Partner with the manufacturer"
        title="Grow your business with a manufacturer behind you."
        body="Architects, interior designers, and prospective franchisees — we're building a dealer network the right way, one territory at a time."
        primaryLabel="Become a Dealer"
        primaryTo="/become-a-dealer"
        secondaryLabel="Contact Sales"
        secondaryTo="/contact"
        testid="home-cta"
      />
    </div>
  );
};

export default HomePage;
